import * as CLOUD_API from "@skydio/api_util/src/backends/cloud_api";
import { OrgPermission } from "@skydio/pbtypes/pbtypes/tools/cloud_api/enums_pb";

import type { ValueType } from "@skydio/core";
import type { resource_group_permission } from "__generated__/UserProfile_loginTypes.graphql";

const { orgPermissionEnum } = CLOUD_API.modules.organizations.utils;

export type ReadableOrgRole = {
  readableName: string;
  enumValue: string | ValueType<OrgPermission.PermEnumMap>;
  enumStringName: string;
};

const ReadableRoleMap: Partial<Record<ValueType<OrgPermission.PermEnumMap>, string>> = {
  [orgPermissionEnum.MEMBER]: "Pilot",
  [orgPermissionEnum.TESTER]: "Test Pilot",
  [orgPermissionEnum.MODERATOR]: "Cloud User",
  [orgPermissionEnum.REMOTE_PILOT]: "Remote Pilot",
  [orgPermissionEnum.ADMIN]: "Organization Admin",
  [orgPermissionEnum.SKYDIO_SUPERUSER]: "Skydio Superuser",
  [orgPermissionEnum.SKYDIO_READONLY]: "Skydio Read-Only",
};

const ReadableRoleMapGql: Partial<Record<resource_group_permission, string>> = {
  MEMBER: "Pilot",
  TESTER: "Test Pilot",
  MODERATOR: "Cloud User",
  REMOTE_PILOT: "Remote Pilot",
  ADMIN: "Organization Admin",
  SKYDIO_SUPERUSER: "Skydio Superuser",
  SKYDIO_READONLY: "Skydio Read-Only",
};

export const getReadableOrgRoleFromName = (role: string) => {
  if (role in OrgPermission.PermEnum) {
    return (
      ReadableRoleMap[OrgPermission.PermEnum[role as keyof typeof OrgPermission.PermEnum]] || ""
    );
  }
  return "";
};

export const getReadableOrgRoleFromGqlPermission = (value: resource_group_permission) =>
  ReadableRoleMapGql[value] || "";

export const getReadableOrgRoleFromValue = (value: ValueType<OrgPermission.PermEnumMap>) =>
  ReadableRoleMap[value] || "";

export const getReadableOrgRoles = (includeRemotePilot = false, includeSkydioRoles = false) => {
  // NOTE(david.taylor): filter here for new org permissions, which relies on underlying ordering of pb enum
  const maxPermission = includeSkydioRoles
    ? orgPermissionEnum.SKYDIO_SUPERUSER
    : orgPermissionEnum.REMOTE_PILOT;
  return (
    Object.entries(orgPermissionEnum)
      .filter(([, value]) => value !== orgPermissionEnum.UNKNOWN) // Filter out the UNKNOWN permission
      // Filter out the TESTER permission, it should no longer be used, but hasn't been fully deleted
      .filter(([, value]) => value !== orgPermissionEnum.TESTER)
      // Filter out future permissions that might be added
      .filter(([, value]) => value <= maxPermission)
      // Filter out the REMOTE_PILOT permission, unless it's explicitly enabled
      .filter(([, value]) => includeRemotePilot || value !== orgPermissionEnum.REMOTE_PILOT)
      .map(
        ([key, value]) =>
          ({
            readableName: getReadableOrgRoleFromValue(
              value as ValueType<OrgPermission.PermEnumMap>
            ),
            enumValue: value,
            enumStringName: key,
          }) as ReadableOrgRole
      )
  );
};

const RolePermissionDescriptionMap: Record<string, string> = {
  [orgPermissionEnum.MEMBER]: "can fly vehicles but can't access Skydio Cloud",
  [orgPermissionEnum.TESTER]: "can fly vehicles but can't access Skydio Cloud", // TODO(jeremys): verify this is correct
  [orgPermissionEnum.MODERATOR]: "can access Skydio Cloud but can't remotely fly vehicles",
  [orgPermissionEnum.REMOTE_PILOT]: "can access Skydio Cloud and remotely fly vehicles",
  [orgPermissionEnum.ADMIN]: "can have full access to the entire organization",
  [orgPermissionEnum.SKYDIO_SUPERUSER]:
    "has full edit access to your entire organization with debugging permissions. Use with caution.",
  [orgPermissionEnum.SKYDIO_READONLY]:
    "has view access to your entire organization with debugging information, but cannot make changes or fly.",
};

export const getOrgRoleDescriptionFromValue = (
  value: ValueType<OrgPermission.PermEnumMap>
): string => {
  return RolePermissionDescriptionMap[value] || "";
};

export const getOrgRoleDescriptionFromName = (role: string): string => {
  if (role in OrgPermission.PermEnum) {
    const enumValue = OrgPermission.PermEnum[role as keyof typeof OrgPermission.PermEnum];
    return getOrgRoleDescriptionFromValue(enumValue);
  }
  return "";
};

export const getResourceGroupDisplayName = (orgName: string, name: string): string => {
  if (name === orgName) {
    return "Entire Organization";
  }
  return name;
};
